import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useMutation } from "@tanstack/react-query";
import { authApi, mapAuthError } from "@/lib/api/auth";
import { forgotPasswordStore } from "@/lib/forgotPasswordStore";
import { resetPasswordSchema, type ResetPasswordValues } from "../schemas/authSchemas";
import { toast } from "@/lib/toast";
import { useI18n } from "@/context/I18nContext";

export function useResetPasswordForm() {
  const router = useRouter();
  const { t } = useI18n();

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { new_password: "", confirm_new_password: "" },
    mode: "onBlur",
  });

  const mutation = useMutation({
    mutationFn: (values: ResetPasswordValues) => {
      const otpToken = forgotPasswordStore.getToken();
      const otpCode = forgotPasswordStore.getCode();
      if (!otpToken || !otpCode) {
        forgotPasswordStore.clear();
        router.replace("/(auth)/forgot-password");
        return Promise.reject(new Error("session_expired"));
      }
      return authApi.resetPassword({
        otp_token: otpToken,
        otp_code: otpCode,
        new_password: values.new_password,
      });
    },
    onSuccess: () => {
      forgotPasswordStore.clear();
      toast.success({ message: t("auth.resetPassword.success") });
      router.replace("/(auth)/login");
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(values).catch(() => {});
  });

  const rawError = mutation.error;
  const error = rawError ? mapAuthError(rawError) : null;

  return {
    form,
    isLoading: mutation.isPending,
    error,
    handleSubmit,
  };
}
